import React from 'react';
import { X, AlertTriangle, ShieldAlert } from 'lucide-react';
import type { WeatherAlert } from '../types';
import { severityColor } from '../services/weatherAlerts';

type RouteAlertsModalProps = {
  alerts: WeatherAlert[];
  onClose: () => void;
};

export const RouteAlertsModal: React.FC<RouteAlertsModalProps> = ({ alerts, onClose }) => {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 970,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sheet-surface"
        style={{
          width: '100%',
          maxWidth: 520,
          borderTopLeftRadius: 36,
          borderTopRightRadius: 36,
          padding: '16px 20px 32px',
          boxSizing: 'border-box',
          maxHeight: '85vh',
          overflowY: 'auto',
          color: 'var(--label)',
        }}
      >
        {/* Grabber */}
        <div
          style={{
            width: 36,
            height: 5,
            borderRadius: 2.5,
            backgroundColor: 'var(--label3)',
            margin: '0 auto 12px',
          }}
        />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: 'rgba(255, 149, 0, 0.15)',
                color: '#FF9500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlertTriangle size={20} />
            </div>
            <div>
              <div className="t-title2">Alerts on your route</div>
              <div className="t-foot c2">{alerts.length} active bulletin{alerts.length === 1 ? '' : 's'}</div>
            </div>
          </div>
          <button className="cbtn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Alerts List */}
        {alerts.length === 0 ? (
          <div className="card-surface" style={{ padding: 24, textAlign: 'center' }}>
            <ShieldAlert size={36} color="var(--label3)" style={{ margin: '0 auto 8px' }} />
            <div className="t-headline">No active alerts</div>
            <div className="t-foot c2" style={{ marginTop: 4 }}>
              Conditions along your route are clear of National Weather Service advisories.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {alerts.map((alert, i) => (
              <div
                key={i}
                className="card-surface"
                style={{
                  padding: 16,
                  borderLeft: `4px solid ${severityColor(alert.severity)}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span className="t-headline">{alert.event}</span>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: severityColor(alert.severity),
                      color: '#000',
                    }}
                  >
                    {alert.severity}
                  </span>
                </div>

                <div className="t-sub" style={{ fontWeight: 500, marginBottom: 6 }}>
                  {alert.headline}
                </div>

                {alert.description && (
                  <div
                    className="t-foot c2"
                    style={{
                      lineHeight: 1.4,
                      whiteSpace: 'pre-line',
                      backgroundColor: 'var(--fill3)',
                      padding: 10,
                      borderRadius: 8,
                    }}
                  >
                    {alert.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
